import { MouseEvent, useState } from 'react'
import { Box, IconButton, Stack, Tooltip, Typography } from '@mui/material'
import { useTranslation } from 'react-i18next'

import CardActionButton from 'components/global/CardActions/CardActionButton'
import ChipStatus from 'packages/ui/ChipStatus'
import { IColumn, ICtx, IItem } from 'packages/ui/Table'

import SsidQrCodeAction from '../../EventSsids/Card/ActionExport'
import { AUTHENTICATION_CONFIG, getAuthenticationTranslationKey } from '../constants'
import { Ssid } from '../types'
import { canEdit } from '../MonitoringSsids'
import { useUser } from '../../../store'

const NetworkCell = ({ ssid }: { ssid: Ssid }) => (
  <Stack direction="row" alignItems="center" spacing={1.25}>
    <Box
      sx={{
        alignItems: 'center',
        backgroundColor: '#F7F7F7',
        borderRadius: '8px',
        color: '#656565',
        display: 'flex',
        flexShrink: 0,
        height: 32,
        justifyContent: 'center',
        width: 32
      }}>
      <i aria-hidden="true" className="fa-light fa-wifi" />
    </Box>
    <Typography component="span" sx={{ color: '#464646', fontSize: 12, fontWeight: 600 }}>
      {ssid.name}
    </Typography>
  </Stack>
)

const AuthenticationCell = ({ ssid }: { ssid: Ssid }) => {
  const { t } = useTranslation()
  const authenticationConfig = AUTHENTICATION_CONFIG[ssid.auth_method]

  return (
    <ChipStatus
      status={t(`ssid_monitoring.authentication.${getAuthenticationTranslationKey(ssid.auth_method)}`)}
      color={authenticationConfig.color}
      bgcolor={authenticationConfig.backgroundColor}
      height="24px"
      borderRadius="50px"
    />
  )
}

const StatusCell = ({ enabled }: { enabled: boolean }) => {
  const { t } = useTranslation()

  return (
    <Stack direction="row" alignItems="center" spacing={0.75}>
      <Box
        sx={{
          backgroundColor: enabled ? '#92BC62' : '#DADDE3',
          borderRadius: '50%',
          height: 7,
          width: 7
        }}
      />
      <Typography component="span" sx={{ color: '#656565', fontSize: 11, fontWeight: 500 }}>
        {t(`ssid_monitoring.status.${enabled ? 'enabled' : 'disabled'}`)}
      </Typography>
    </Stack>
  )
}

const PassphraseCell = ({ passphrase }: { passphrase: string | null }) => {
  const [isVisible, setIsVisible] = useState(false)
  const { t } = useTranslation()

  if (!passphrase) {
    return (
      <Typography sx={{ color: '#989898', fontSize: 12, fontWeight: 400 }}>
        {t('ssid_monitoring.no_passphrase')}
      </Typography>
    )
  }

  const handleToggleVisibility = () => {
    setIsVisible(previousValue => !previousValue)
  }

  return (
    <Stack direction="row" alignItems="center" spacing={1}>
      <Typography
        component="span"
        sx={{
          color: '#656565',
          fontFamily: isVisible ? 'monospace' : 'inherit',
          fontSize: 12,
          fontWeight: 400,
          letterSpacing: isVisible ? 0 : '2px'
        }}>
        {isVisible ? passphrase : '••••••••'}
      </Typography>
      <Tooltip title={t(isVisible ? 'ssid_monitoring.passphrase.hide' : 'ssid_monitoring.passphrase.show')} arrow>
        <IconButton
          size="small"
          aria-label={t(isVisible ? 'ssid_monitoring.passphrase.hide' : 'ssid_monitoring.passphrase.show')}
          onClick={handleToggleVisibility}
          sx={{
            border: '1px solid #EFEFEF',
            borderRadius: '8px',
            height: 26,
            width: 26
          }}>
          <i
            aria-hidden="true"
            className={`fa-light fa-${isVisible ? 'eye-slash' : 'eye'}`}
            style={{ color: '#656565', fontSize: 12 }}
          />
        </IconButton>
      </Tooltip>
    </Stack>
  )
}

const ActionsCell = ({ ssid, context }: { ssid: Ssid; context?: ICtx }) => {
  const { t } = useTranslation()
  const [user] = useUser()
  const handleEdit = (event: MouseEvent<HTMLDivElement>) => {
    if (canEdit(user)) {
      context?.onSendAction?.(event, 'edit', ssid as unknown as IItem)
    }
  }

  return (
    <Stack direction="row" alignItems="center" spacing={0.5}>
      {(ssid.auth_method === '802.1x' || (ssid.auth_method === 'psk' && Boolean(ssid.passphrase))) && (
        <SsidQrCodeAction
          ssid={{
            authMethod: ssid.auth_method,
            label: ssid.name,
            name: ssid.name,
            passphrase: ssid.passphrase
          }}
        />
      )}
      {
        canEdit(user) && (
          <CardActionButton
            iconlabel="pen"
            tooltiptitle={t('ssid_monitoring.actions.edit')}
            onClick={handleEdit}
            style={{
              border: '1px solid #EFEFEF',
              borderRadius: '8px',
              height: '24px',
              minHeight: '24px',
              minWidth: '24px',
              width: '24px'
            }}
          />
        )
      }
    </Stack>
  )
}

const COLUMNS_CONFIG: IColumn[] = [
  {
    name: 'name',
    header: {
      label: context => context.t('ssid_monitoring.table.network')
    },
    style: {
      minWidth: 220
    },
    value: item => <NetworkCell ssid={item as unknown as Ssid} />
  },
  {
    name: 'auth_method',
    header: {
      label: context => context.t('ssid_monitoring.table.authentication')
    },
    style: {
      minWidth: 150
    },
    value: item => <AuthenticationCell ssid={item as unknown as Ssid} />
  },
  {
    name: 'enabled',
    header: {
      label: context => context.t('ssid_monitoring.table.status')
    },
    style: {
      minWidth: 120
    },
    value: item => <StatusCell enabled={(item as unknown as Ssid).enabled} />
  },
  {
    name: 'vlan',
    header: {
      label: context => context.t('ssid_monitoring.table.vlan')
    },
    style: {
      minWidth: 100
    },
    value: item => (
      <Typography component="span" sx={{ color: '#656565', fontSize: 12, fontWeight: 400 }}>
        {(item as unknown as Ssid).vlan ?? '—'}
      </Typography>
    )
  },
  {
    name: 'passphrase',
    header: {
      label: context => context.t('ssid_monitoring.table.passphrase')
    },
    style: {
      minWidth: 180
    },
    value: item => <PassphraseCell passphrase={(item as unknown as Ssid).passphrase} />
  },
  {
    name: 'actions',
    header: {
      label: () => ''
    },
    style: {
      minWidth: 72,
      width: 72
    },
    value: (item, context) => <ActionsCell ssid={item as unknown as Ssid} context={context} />
  }
]

export default COLUMNS_CONFIG
